import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Plus, Save, Globe, Trash2, Languages, Edit2 } from "lucide-react";

export default function AdminTranslations() {
  const [keys, setKeys] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [showAddKey, setShowAddKey] = useState(false);
  const [showAddLang, setShowAddLang] = useState(false);
  const [newKey, setNewKey] = useState({ key: "", nl: "", en: "", fr: "" });
  const [newLang, setNewLang] = useState({ code: "", name: "", flag: "" });

  const fetchData = useCallback(() => {
    const params = new URLSearchParams({ page: page.toString(), limit: "30" });
    if (search) params.set("search", search);
    Promise.all([
      api.get(`/translations/admin/keys?${params}`),
      api.get("/translations/admin/languages"),
    ]).then(([k, l]) => {
      setKeys(k.data.keys);
      setTotal(k.data.total);
      setLanguages(l.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveKey = async () => {
    try {
      const updates = {};
      languages.forEach((l) => { if (editingKey[l.code] !== undefined) updates[l.code] = editingKey[l.code]; });
      await api.put(`/translations/admin/keys/${editingKey.id}`, updates);
      toast.success("Vertaling opgeslagen");
      setEditingKey(null);
      fetchData();
    } catch { toast.error("Fout"); }
  };

  const handleAddKey = async () => {
    try {
      await api.post("/translations/admin/keys", newKey);
      toast.success("Key toegevoegd");
      setShowAddKey(false);
      setNewKey({ key: "", nl: "", en: "", fr: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Fout"); }
  };

  const handleDeleteKey = async (id) => {
    if (!window.confirm("Vertaalsleutel verwijderen?")) return;
    await api.delete(`/translations/admin/keys/${id}`);
    toast.success("Verwijderd");
    fetchData();
  };

  const handleAddLanguage = async () => {
    try {
      const res = await api.post("/translations/admin/languages", newLang);
      toast.success(`Taal ${res.data.code} toegevoegd - ${res.data.keys_to_translate} keys te vertalen`);
      setShowAddLang(false);
      setNewLang({ code: "", name: "", flag: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Fout"); }
  };

  const activeLangs = languages.filter((l) => l.active);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Laden...</div>;

  return (
    <div className="space-y-6" data-testid="admin-translations-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Languages className="w-6 h-6 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold">Vertaalplatform</h1>
            <p className="text-sm text-muted-foreground">{total} vertalingen, {activeLangs.length} talen</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showAddLang} onOpenChange={setShowAddLang}>
            <DialogTrigger asChild><Button variant="outline" size="sm"><Globe className="w-3.5 h-3.5 mr-1.5" />Taal Toevoegen</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nieuwe Taal Toevoegen</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-sm text-muted-foreground">De nieuwe taal wordt automatisch toegevoegd aan de taalwisselaar. Alle bestaande vertalingen krijgen een leeg veld dat je kunt invullen.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Code (2 letters)</Label><Input value={newLang.code} onChange={(e) => setNewLang({...newLang, code: e.target.value.toLowerCase().slice(0, 2)})} placeholder="de" maxLength={2} className="mt-1" /></div>
                  <div><Label>Naam</Label><Input value={newLang.name} onChange={(e) => setNewLang({...newLang, name: e.target.value})} placeholder="Deutsch" className="mt-1" /></div>
                  <div><Label>Vlag</Label><Input value={newLang.flag} onChange={(e) => setNewLang({...newLang, flag: e.target.value.toUpperCase().slice(0, 2)})} placeholder="DE" maxLength={2} className="mt-1" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddLang(false)}>Annuleren</Button>
                <Button onClick={handleAddLanguage} className="bg-blue-600">Toevoegen</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={showAddKey} onOpenChange={setShowAddKey}>
            <DialogTrigger asChild><Button className="bg-[#2CA8AC] hover:bg-[#248F92]" size="sm"><Plus className="w-3.5 h-3.5 mr-1.5" />Key Toevoegen</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nieuwe Vertaalsleutel</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label>Key (bijv. nav.dashboard)</Label><Input value={newKey.key} onChange={(e) => setNewKey({...newKey, key: e.target.value})} className="mt-1 font-mono-data" /></div>
                {activeLangs.map((l) => (
                  <div key={l.code}><Label>{l.name} ({l.code.toUpperCase()})</Label><Input value={newKey[l.code] || ""} onChange={(e) => setNewKey({...newKey, [l.code]: e.target.value})} className="mt-1" /></div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddKey(false)}>Annuleren</Button>
                <Button onClick={handleAddKey} className="bg-blue-600">Toevoegen</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Languages overview */}
      <div className="flex flex-wrap gap-2">
        {languages.map((l) => (
          <Badge key={l.code} variant={l.active ? "default" : "secondary"} className={`py-1.5 px-3 ${l.active ? "bg-blue-600" : ""}`}>
            {l.flag} {l.name}
          </Badge>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Zoek op key of vertaling..." className="pl-9" />
      </div>

      {/* Translation keys table */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Key</TableHead>
                {activeLangs.map((l) => (
                  <TableHead key={l.code}>{l.flag} {l.name}</TableHead>
                ))}
                <TableHead className="w-[80px] text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id} data-testid={`translation-${k.key}`}>
                  <TableCell className="font-mono-data text-xs text-blue-400">{k.key}</TableCell>
                  {activeLangs.map((l) => (
                    <TableCell key={l.code} className="text-sm">
                      {k[l.code] ? (
                        <span className="truncate block max-w-[200px]">{k[l.code]}</span>
                      ) : (
                        <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">Ontbreekt</Badge>
                      )}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingKey({...k})}><Edit2 className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteKey(k.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 30 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Vorige</Button>
          <span className="text-sm text-muted-foreground py-2">Pagina {page} van {Math.ceil(total / 30)}</span>
          <Button variant="outline" size="sm" disabled={keys.length < 30} onClick={() => setPage((p) => p + 1)}>Volgende</Button>
        </div>
      )}

      {/* Edit key dialog */}
      {editingKey && (
        <Dialog open={!!editingKey} onOpenChange={() => setEditingKey(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Vertaling bewerken: <code className="text-blue-400 ml-2">{editingKey.key}</code></DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              {activeLangs.map((l) => (
                <div key={l.code}>
                  <Label className="flex items-center gap-2">{l.flag} {l.name} <span className="text-xs text-muted-foreground">({l.code})</span></Label>
                  <Input value={editingKey[l.code] || ""} onChange={(e) => setEditingKey({...editingKey, [l.code]: e.target.value})} className="mt-1" />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingKey(null)}>Annuleren</Button>
              <Button onClick={handleSaveKey} className="bg-blue-600"><Save className="w-4 h-4 mr-2" />Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
